/**
 * Satellite room grouping.
 *
 * A small closet / pantry / nook carved off a larger room by the
 * processing pipeline is stamped with `parentRoomId` pointing at its
 * HOST (see `functions/features/room-polygon-build/
 * 8.13-satellite-sub-rooms.js`). It keeps its own polygon, walls and
 * ceiling, but reads as an appendage of the host rather than a peer
 * room: it carries no name, it is never separately selectable, and
 * selecting either one highlights both.
 *
 * Pure helpers — no React, no Firestore. Every consumer goes through
 * these two so the grouping rule lives in one place.
 */

/** Is this room an appendage of another room? */
export function isSatelliteRoom(room) {
  return typeof room?.parentRoomId === 'string' && room.parentRoomId !== '';
}

/**
 * The room a pick should actually land on: a satellite redirects to its
 * host, anything else is itself.
 *
 * Falls back to the room as given when the host isn't in `rooms` (a
 * story filter that dropped it, a partially-loaded list) — better to
 * select the satellite than to select nothing. The pipeline guarantees
 * a host is never itself a satellite, so this never needs to loop.
 *
 * @param {object|null} room
 * @param {Array<object>} rooms All rooms in scope.
 * @returns {object|null}
 */
export function resolveHostRoom(room, rooms) {
  if (!isSatelliteRoom(room)) return room;
  const host = (rooms || []).find((r) => r?.id === room.parentRoomId);
  return host || room;
}

/**
 * The id-set that should light up / stay visible when `room` is the
 * selection: the host plus every satellite attached to it.
 *
 * Accepts a satellite as well as a host — it resolves first — so
 * callers don't have to normalize twice. Returns an empty set for no
 * selection, which reads as "nothing is selected" at every call site.
 *
 * @param {object|null} room
 * @param {Array<object>} rooms
 * @returns {Set<string>}
 */
export function roomGroupIds(room, rooms) {
  const host = resolveHostRoom(room, rooms);
  if (!host?.id) return new Set();
  const ids = new Set([host.id]);
  for (const r of rooms || []) {
    if (r?.parentRoomId === host.id && r.id) ids.add(r.id);
  }
  return ids;
}

/**
 * The group as room OBJECTS, for callers that need geometry (a camera fit
 * over the union of the group's polygons) rather than ids.
 *
 * `room` itself comes first and is kept as given — hosts routinely pass a
 * live-edited or partially-hydrated copy that `rooms` doesn't hold — with
 * the other members read from `rooms`. Returns `[]` for no selection, and
 * `[room]` for a room with no satellites.
 *
 * @param {object|null} room
 * @param {Array<object>} rooms
 * @returns {Array<object>}
 */
export function roomGroupMembers(room, rooms) {
  if (!room) return [];
  const ids = roomGroupIds(room, rooms);
  return [
    room,
    ...(rooms || []).filter((r) => r?.id && r.id !== room.id && ids.has(r.id)),
  ];
}
